import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEffect } from 'react';

export interface PaymentTransactionSummary {
  id: string;
  payment_id: string;
  transaction_date: string;
  amount: number;
  payment_method: string;
  reference_number?: string | null;
  notes?: string | null;
  status: string;
}

export interface UnlinkedTransaction {
  id: string;
  deal_id: string;
  contact_id?: string | null;
  transaction_date: string;
  amount: number;
  currency?: string | null;
  payment_method: string;
  reference_number?: string | null;
  notes?: string | null;
  status: string;
  stripe_payment_intent_id?: string | null;
  created_at: string;
}

export interface PaymentTransactionsByDealResult {
  grouped: Record<string, PaymentTransactionSummary[]>;
  unlinked: UnlinkedTransaction[];
}

/**
 * Fetches all payment transactions for a deal, grouped by payment_id.
 * Also returns unlinked transactions (payment_id = null) separately.
 */
export function usePaymentTransactionsByDeal(dealId?: string) {
  const queryClient = useQueryClient();

  // Realtime subscription para atualizar automaticamente quando transações mudarem
  useEffect(() => {
    if (!dealId) return;

    const channel = supabase
      .channel(`payment-transactions-deal-${dealId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payment_transactions',
          filter: `deal_id=eq.${dealId}`,
        },
        () => {
          queryClient.invalidateQueries({ 
            queryKey: ['payment-transactions-by-deal', dealId] 
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [dealId, queryClient]);

  return useQuery({
    queryKey: ['payment-transactions-by-deal', dealId],
    queryFn: async (): Promise<PaymentTransactionsByDealResult> => {
      if (!dealId) return { grouped: {}, unlinked: [] };

      const { data, error } = await supabase
        .from('payment_transactions')
        .select('id, payment_id, deal_id, contact_id, transaction_date, amount, currency, payment_method, reference_number, notes, status, stripe_payment_intent_id, created_at')
        .eq('deal_id', dealId)
        .order('transaction_date', { ascending: false });

      if (error) throw error;

      // Separate linked and unlinked transactions
      const grouped: Record<string, PaymentTransactionSummary[]> = {};
      const unlinked: UnlinkedTransaction[] = [];

      for (const tx of (data || [])) {
        if (tx.payment_id) {
          if (!grouped[tx.payment_id]) {
            grouped[tx.payment_id] = [];
          }
          grouped[tx.payment_id].push(tx as PaymentTransactionSummary);
        } else {
          unlinked.push(tx as UnlinkedTransaction);
        }
      }

      return { grouped, unlinked };
    },
    enabled: !!dealId,
    staleTime: 30000,
  });
}
